/**
 * Persistent World Memory — local-first, no network.
 *
 * Remembers cross-match emotional landmarks of the tournament:
 *   - dominant nation of the day
 *   - top fan globally
 *   - comeback of the tournament
 *   - most-shared viral capsule
 *   - most chaotic match
 *
 * All writes are idempotent / monotone (only replaced when the new
 * candidate beats the stored score). Read-side returns the cached
 * snapshot — components consume it without triggering any I/O.
 */

const KEY = "vs_world_memory_v1";

export interface WorldMemoryEntry {
  key: string;
  label: string;
  score: number;
  updatedAt: number;
}

export interface WorldMemory {
  dominantNation?: WorldMemoryEntry;
  topFan?: WorldMemoryEntry;
  tournamentComeback?: WorldMemoryEntry;
  topCapsule?: WorldMemoryEntry;
  chaoticMatch?: WorldMemoryEntry;
}

function read(): WorldMemory {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as WorldMemory;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function write(m: WorldMemory): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* quota / private mode — silent */
  }
}

export function getWorldMemory(): WorldMemory {
  return read();
}

type Slot = keyof WorldMemory;

function bump(slot: Slot, entry: WorldMemoryEntry): WorldMemory {
  const m = read();
  const cur = m[slot];
  if (!cur || entry.score >= cur.score) {
    m[slot] = { ...entry, updatedAt: Date.now() };
    write(m);
  }
  return m;
}

export const recordDominantNation = (key: string, label: string, score: number) =>
  bump("dominantNation", { key, label, score, updatedAt: Date.now() });

export const recordTopFan = (key: string, label: string, score: number) =>
  bump("topFan", { key, label, score, updatedAt: Date.now() });

export const recordTournamentComeback = (key: string, label: string, score: number) =>
  bump("tournamentComeback", { key, label, score, updatedAt: Date.now() });

export const recordTopCapsule = (key: string, label: string, score: number) =>
  bump("topCapsule", { key, label, score, updatedAt: Date.now() });

export const recordChaoticMatch = (key: string, label: string, score: number) =>
  bump("chaoticMatch", { key, label, score, updatedAt: Date.now() });